import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ToggleSection } from "@/components/form/ToggleSection";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { NumberInputRow } from "@/components/form/NumberInputRow";
import type { RateMultiplier } from '@/types';
import { premiumTypeOptions } from '@/constants/premiumShiftTypes';
import { cn } from '@/lib/utils';
import { fieldLabelClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

interface PremiumShiftOption {
  value: 'multiplier_offset' | 'fixed' | 'fixed_offset' | null;
  label: string;
  description: string;
}

export interface PremiumShiftConfigFormProps {
  allowPremiumShifts: boolean;
  onAllowPremiumShiftsChange: (checked: boolean) => void;
  premiumShiftOffsetMode: 'multiplier_offset' | 'fixed' | 'fixed_offset' | null;
  onPremiumShiftOffsetModeChange: (mode: 'multiplier_offset' | 'fixed' | 'fixed_offset' | null) => void;
  premiumShiftMultiplierCode: string | null;
  onPremiumShiftMultiplierCodeChange: (code: string) => void;
  rateMultipliers: RateMultiplier[];
  hasAdditionalMultipliers: boolean;
  fixedOffsetInputProps: Record<string, unknown>;
  fixedOffsetError?: React.ReactNode;
  multiplierSelectionError?: React.ReactNode;
  isCustomized?: boolean;
  onRevert?: () => void;
  title?: string;
  description?: string;
  noCard?: boolean;
  context?: 'user' | 'client';
}

const PremiumShiftConfigFormComponent: React.FC<PremiumShiftConfigFormProps> = ({
  allowPremiumShifts,
  onAllowPremiumShiftsChange,
  premiumShiftOffsetMode,
  onPremiumShiftOffsetModeChange,
  premiumShiftMultiplierCode,
  onPremiumShiftMultiplierCodeChange,
  rateMultipliers,
  hasAdditionalMultipliers,
  fixedOffsetInputProps,
  fixedOffsetError,
  multiplierSelectionError,
  isCustomized,
  onRevert,
  title = "Premium Shifts",
  description = "Configure default premium pay for holidays, overnights, and other premium shifts.",
  noCard,
  context = 'user',
}) => {
  const content = (
    <div className="space-y-4">
      {!noCard && (
        <div className="space-y-1 text-center mb-4">
          <div className="flex items-center justify-center gap-3">
            <h3 className="text-lg font-medium text-foreground">{title}</h3>
            {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
          </div>
          <p className="text-sm text-muted-foreground px-2">{description}</p>
        </div>
      )}
        <ToggleSection
          id="allowPremiumShifts"
          label="Allow Premium Shifts"
          checked={allowPremiumShifts}
          onCheckedChange={onAllowPremiumShiftsChange}
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="premiumMode" className={fieldLabelClass}>Default Premium Type</Label>
              <div id="premiumMode" className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1" role="group">
                <TooltipProvider delayDuration={300}>
                  {premiumTypeOptions.map((option: PremiumShiftOption) => {
                    const isDisabled =
                      !hasAdditionalMultipliers &&
                      (option.value === "multiplier_offset" ||
                       option.value === "fixed_offset");

                    const Btn = (
                      <Button
                        key={option.value}
                        variant={
                          premiumShiftOffsetMode === option.value
                            ? "secondary"
                            : "outline"
                        }
                        className="w-full text-center py-2 px-3"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                             onPremiumShiftOffsetModeChange(option.value);
                          }
                        }}
                      >
                        {option.label}
                      </Button>
                    );

                    return isDisabled ? (
                      <Tooltip key={option.value}>
                        <TooltipTrigger asChild>
                          <span className="w-full">
                            {React.cloneElement(Btn, { className: cn(Btn.props.className, "pointer-events-none") })}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-center">
                          Add at least one additional rate multiplier to enable this option.
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      Btn
                    );
                  })}
                </TooltipProvider>
                {premiumTypeOptions.map((option) => (
                  <div key={`${option.value}-description`} className="col-span-1 sm:col-span-2">
                    {premiumShiftOffsetMode === option.value && (
                      <p className="text-sm text-muted-foreground mt-1 mb-3 animate-fade-in">
                        {option.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {premiumShiftOffsetMode === 'fixed' && (
              <NumberInputRow
                id="premiumFixed"
                label="Fixed Rate Premium"
                unit="$"
                unitPosition="left"
                placeholder="e.g. 100"
                inputProps={{
                  ...fixedOffsetInputProps,
                  step: 1,
                  min: 0,
                  onWheel: (e: React.WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur(),
                }}
                errorElement={fixedOffsetError}
                description="Extra flat amount added to each premium shift."
              />
            )}

            {(premiumShiftOffsetMode === 'multiplier_offset' || 
              premiumShiftOffsetMode === 'fixed_offset') && (
              <div className="space-y-2">
                <Label className={fieldLabelClass}>
                  {premiumShiftOffsetMode === 'multiplier_offset'
                    ? 'Starting Rate Multiplier'
                    : 'Fixed Rate Multiplier'}
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="group">
                  {rateMultipliers
                    .filter(m => m.code !== 'STRAIGHT_TIME')
                    .sort((a, b) => (a.defaultMultiplier || 0) - (b.defaultMultiplier || 0))
                    .map((multiplier) => (
                      <Button
                        key={multiplier.code}
                        variant="outline"
                        className={cn(
                          "w-full text-left py-2 px-3",
                          "flex items-center justify-between",
                          premiumShiftMultiplierCode === multiplier.code && "bg-secondary"
                        )}
                        onClick={() => onPremiumShiftMultiplierCodeChange(multiplier.code)}
                      >
                        <span>{multiplier.name}</span>
                        <span className="text-muted-foreground">
                          {multiplier.defaultMultiplier}×
                        </span>
                      </Button>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {premiumShiftOffsetMode === 'multiplier_offset'
                    ? 'Premium shifts will start at this rate and progress normally.'
                    : 'All hours during premium shifts will be paid starting at this fixed multiplier rate.'}
                </p>
                {multiplierSelectionError}
              </div>
            )}
          </div>
        </ToggleSection>
        {isCustomized && onRevert && (
          <div className="pt-4 border-t flex justify-center">
            <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
              Revert to Profile Defaults
            </Button>
          </div>
        )}
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card
      className="animate-fade-in shadow-sm rounded-xl p-3 pb-4 sm:p-6 sm:max-w-3xl sm:mx-auto"
    >
      {content}
    </Card>
  );
};

export const PremiumShiftConfigForm = React.memo(PremiumShiftConfigFormComponent);
PremiumShiftConfigForm.displayName = 'PremiumShiftConfigForm';
